import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Instagram } from "lucide-react";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { to: "/", label: "Início" },
    { to: "/sobre", label: "Sobre" },
    { to: "/servicos", label: "Serviços" },
    { to: "/planos", label: "Planos" },
    { to: "/mentoria", label: "Mentoria" },
    { to: "/contato", label: "Contato" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "glass-navbar shadow-sm py-3"
          : "bg-transparent py-5"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <nav className="flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2"
            data-testid="navbar-logo"
          >
            <span className="font-serif text-xl md:text-2xl font-semibold text-brand-neutro tracking-tight">
              Lylian Lopes
            </span>
            <span className="hidden sm:inline font-sans text-sm text-brand-muted font-light">
              Consultoria
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`font-sans text-sm transition-premium link-underline ${
                  isActive(link.to)
                    ? "text-brand-terracota font-medium"
                    : "text-brand-neutro hover:text-brand-terracota"
                }`}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA + Social */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="https://instagram.com/lylopespsi"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-neutro hover:text-brand-terracota transition-premium"
              data-testid="nav-instagram"
            >
              <Instagram size={20} strokeWidth={1.5} />
            </a>
            <Link
              to="/contato"
              className="btn-primary px-5 py-2.5 rounded-full font-sans text-sm font-medium"
              data-testid="nav-cta-button"
            >
              Agendar Diagnóstico
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-brand-neutro p-2"
            data-testid="mobile-menu-button"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            className="lg:hidden absolute top-full left-0 right-0 bg-brand-base-clara border-t border-brand-border shadow-lg animate-fade-in"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`block font-sans text-base py-2 ${
                    isActive(link.to)
                      ? "text-brand-terracota font-medium"
                      : "text-brand-neutro"
                  }`}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-4 border-t border-brand-border">
                <Link
                  to="/contato"
                  className="btn-primary block text-center px-5 py-3 rounded-full font-sans text-sm font-medium"
                  data-testid="mobile-cta-button"
                >
                  Agendar Diagnóstico
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
